import React, { PureComponent } from "react"
import PropTypes from "prop-types"
import ReactDOM from "react-dom"
import { Radium } from "higher-order-components"
import Measure from "react-measure"
import inView from "in-view"
import { HAS_TOUCH } from "constants/environment"
import { rafThrottle } from "utils"
import { MuteIcon, UnMuteIcon } from "components"
import debounce from 'AtomicUtils/debounce'
import { WINDOW } from "AtomicUtils/environment"

/** Renders a video background component.
 * @param {Url} src - Href Value.
 * @param {Bool} relative - Position.
 * @param {String} poster - Label Value.
 * @returns {ReactElement} react component
 */

class VideoBackground extends PureComponent {
  constructor() {
    super()
    this.state = {
      videoRatio: 0,
      wrapperRatio: 0,
      inview: false,
      canPlay: false,
      componentMounted: false,
      muted: true
    }
  }
  componentDidMount() {
    this.setState({
      componentMounted:true
    },() =>{
      this.domNode = ReactDOM.findDOMNode(this)
      this.scrollHandler = rafThrottle(() => this.inViewHandler())
      this.inViewHandler()
      this.handleFrame()
      WINDOW.addEventListener("scroll", debounce(this.scrollHandler, 250), {
        passive: true
      })
    })
  }
  componentWillUnmount() {
    WINDOW.removeEventListener("scroll", debounce(this.scrollHandler,250), {
      passive: true
    });
    this.scrollHandler && this.scrollHandler.cancel()
  }
  inViewHandler() {
    if (!this.domNode) return
    if (this.props.isContainerInView) inView.offset({ right: -99999, left: -99999 })
    if (inView.is(this.domNode)) this.setState({ inview: true })
    else if (!this.state.fade) this.setState({ inview: false })
  }

  handleFrame = () => {
    if (this.props.updateCurrentFrame){
      setInterval(()=>{
        this.props.updateCurrentFrame(this.video)
      }, 100)
    }
  }

  render() {
    if (!this.state.componentMounted) return <div></div>
    const {
      muted,
      src,
      onLoadedMetadata,
      relative,
      useInView = true,
      soundColor = "white",
      soundDirection = "left",
      getVideoRef = () => {}
    } = this.props
    const { wrapperRatio } = this.state
    return (
      <Measure
        onResize={({ entry }) => {
          if (wrapperRatio !== entry.width / entry.height) {
            this.setState({
              wrapperRatio: entry.width / entry.height
            }, () => {
              this.props.getDimensionsChild && this.props.getDimensionsChild({
                width: this.video.offsetWidth,
                height: this.video.offsetHeight
              })
            })
          } else {
            this.props.getDimensionsChild && this.props.getDimensionsChild({
              width: this.video.offsetWidth,
              height: this.video.offsetHeight
            })
          }
        }}>
        {({ measureRef }) => (
          <div
            ref={measureRef}
            style={{
              position: relative ? "relative" : "absolute",
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
              top: 0,
              left: 0
            }}>
            {(!useInView || this.state.inview) && <video
              muted={HAS_TOUCH || muted || this.state.muted}
              autoPlay
              playsInline
              ref={(element) => {
                this.video = element
                getVideoRef(element)
              }}
              loop={this.props.loop}
              poster={this.props.poster}
              onLoadedMetadata={(element) => {
                onLoadedMetadata && onLoadedMetadata(element)
                const { target } = element
                this.setState({ videoRatio: target.videoWidth / target.videoHeight })
              }}
              onLoadedData={() => { this.setState({ canPlay: true }) }}
              onTimeUpdate={(element) => {
                this.setState({ canPlay: true })
              }}
              style={relative ? styles.relative : styles.absolute}
              src={src}
              type="video/mp4" />}
            {this.props.showSoundIcon && this.state.canPlay && !muted && !HAS_TOUCH &&
              <div
                onClick={() => {
                  this.setState({
                    muted: !this.state.muted
                  })
                }}
                style={styles.icon(soundColor, soundDirection)}>
                {this.state.muted && <MuteIcon />}
                {!this.state.muted && <UnMuteIcon />}
              </div>
            }
          </div>
        )}
      </Measure>
    )
  }
}
VideoBackground.propTypes = {
  onLoadedMetadata: PropTypes.func,
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  soundDirection: PropTypes.string,
  soundColor: PropTypes.string,
  getRef: PropTypes.func,
  relative: PropTypes.bool,
  showSoundIcon: PropTypes.bool,
  muted: PropTypes.bool,
  isContainerInView: PropTypes.bool,
  loop: PropTypes.bool
}

VideoBackground.defaultProps = {
  loop: true,
  showSoundIcon: false
}

export default Radium(VideoBackground)

const styles = {
  absolute: {
    position: "absolute",
    left: "0",
    top: "0",
    minHeight: "100%",
    minWidth: "100%",
    zIndex: -1,
    objectFit: "cover",
    width: "100%",
    height: "100%"
  },
  relative: {
    maxWidth: "100%",
    maxHeight: "100%",
    position: "relative",
    height: "auto"
  },
  icon: (soundColor, soundDirection) => ({
    position: "absolute",
    width: "40px",
    height: "40px",
    bottom: 0,
    right: soundDirection==="right"?0:"auto",
    left: soundDirection==="left"?0:"auto",
    padding: "30px",
    color: soundColor,
    cursor: "pointer"
  })
}



// WEBPACK FOOTER //
// ./src/components/video-background/index.jsx